// src/utils/adminTestHelper.ts
import adminService from '../services/adminService';
import unifiedAuth from './unifiedAuth';
import { config } from '../config';

const API_BASE_URL = config.apiBaseUrl;

export class AdminTestHelper {
  /**
   * Make the current user an admin for testing purposes
   */
  static async makeCurrentUserAdmin(): Promise<void> {
    const token = unifiedAuth.getAccessToken();
    if (!token) {
      throw new Error('User must be logged in first');
    }

    const response = await unifiedAuth.fetch(`${API_BASE_URL}/api/admin/make-me-admin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to make user admin: ${response.status} ${response.statusText}`);
    }

    const _result = await response.json();
    // Admin grant result logged
    
    // User needs to refresh their session to get updated admin privileges
    alert('You have been granted admin privileges! Please refresh the page to access admin features.');
  }

  /**
   * Test admin dashboard endpoints
   */
  static async testAdminEndpoints(): Promise<void> {
    // Testing Admin Dashboard Integration
    
    try {
      // Test authentication status
      // Testing admin authentication...
      const hasAdminAccess = adminService.hasAdminAccess();
      // Admin access status checked

      if (!hasAdminAccess) {
        // Admin access denied. User may need admin privileges.
        return;
      }

      // Test dashboard stats
      // Testing dashboard stats...
      const _stats = await adminService.getDashboardStats();
      // Dashboard stats retrieved

      // Test content management
      // Testing content management...
      const _content = await adminService.getContentManagement({ page: 1, page_size: 5 });
      // Content management tested

      // Test user management
      // Testing user management...
      const _users = await adminService.getUserManagement({ page: 1, page_size: 5 });
      // User management tested

      // Test admin activities
      // Testing admin activities...
      const _activities = await adminService.getAdminActivities(1, 5);
      // Admin activities tested

      // All admin endpoints tested successfully!

    } catch (error) {
      console.error('❌ Admin endpoint test failed:', error);
      
      if (error instanceof Error && error.message.includes('Admin privileges required')) {
        // Tip: Use AdminTestHelper.makeCurrentUserAdmin() to grant admin privileges
      }
    }
  }

  /**
   * Test admin dashboard data integrity
   */
  static async validateDashboardData(): Promise<void> {
    console.log('=== Validating Dashboard Data Integrity ===');
    
    try {
      const stats = await adminService.getDashboardStats();
      
      // Validate user stats
      console.log('Checking user statistics...');
      if (typeof stats.total_users !== 'number' || stats.total_users < 0) {
        console.warn('⚠️ Invalid total_users value:', stats.total_users);
      } else {
        console.log('✅ User stats valid');
      }

      // Validate content stats  
      console.log('Checking content statistics...');
      const contentTypes = ['total_cinema', 'total_content', 'total_podcasts', 'total_animations', 'total_sneak_peeks'];
      let totalCalculated = 0;
      
      for (const type of contentTypes) {
        const value = stats[type as keyof typeof stats] as number;
        if (typeof value !== 'number' || value < 0) {
          console.warn(`⚠️ Invalid ${type} value:`, value);
        } else {
          totalCalculated += value;
        }
      }

      // Validate originals calculation (should be sum of all content types)
      if (Math.abs(stats.total_originals - totalCalculated) > 0) {
        console.warn('⚠️ Originals count mismatch. Expected:', totalCalculated, 'Got:', stats.total_originals);
      } else {
        console.log('✅ Originals calculation correct');
      }

      // Validate engagement stats
      console.log('Checking engagement statistics...');
      const engagementStats = ['total_views', 'total_likes', 'total_comments'] as const;
      for (const stat of engagementStats) {
        const value = stats[stat];
        if (typeof value !== 'number' || value < 0) {
          console.warn(`⚠️ Invalid ${stat} value:`, value);
        }
      }
      console.log('✅ Engagement stats validated');

      console.log('🎉 Dashboard data validation complete!');

    } catch (error) {
      console.error('❌ Dashboard data validation failed:', error);
    }
  }

  /**
   * Test user management endpoints
   */
  static async testUserManagement(): Promise<void> {
    console.log('🧪 Testing user management functionality...');
    
    try {
      const token = unifiedAuth.getAccessToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const headers = {
        'Content-Type': 'application/json'
      };

      // Test getting all users
      console.log('Testing user listing...');
      const usersResponse = await unifiedAuth.fetch(`${API_BASE_URL}/api/admin/users/`, { headers });
      
      if (usersResponse.ok) {
        const userData = await usersResponse.json();
        console.log('✅ User listing successful');
        console.log(`📊 Total users: ${userData.data.total_count}`);

        // Test search functionality
        const searchResponse = await unifiedAuth.fetch(`${API_BASE_URL}/api/admin/users/?search=test`, { headers });
        if (searchResponse.ok) {
          console.log('✅ User search working');
        }

        // Test status filtering
        const adminResponse = await unifiedAuth.fetch(`${API_BASE_URL}/api/admin/users/?status=admin`, { headers });
        if (adminResponse.ok) {
          const adminData = await adminResponse.json();
          console.log(`✅ Admin filtering working - ${adminData.data.users.length} admin users`);
        }

        const pendingResponse = await unifiedAuth.fetch(`${API_BASE_URL}/api/admin/users/?status=pending`, { headers });
        if (pendingResponse.ok) {
          const pendingData = await pendingResponse.json();
          console.log(`✅ Pending filtering working - ${pendingData.data.users.length} pending users`);
        }

      } else {
        console.error('❌ User listing failed:', usersResponse.status);
      }
    } catch (error) {
      console.error('❌ Error testing user management:', error);
    }
  }

  /**
   * Test user creation functionality
   */
  static async testUserCreation(): Promise<{ id: string; username: string } | null> {
    console.log('🧪 Testing user creation...');
    
    const testUser = {
      username: 'testuser_' + Date.now(),
      email: 'test_' + Date.now() + '@example.com',
      first_name: 'Test',
      last_name: 'User',
      password: 'testpassword123'
    };
    
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/api/admin/users/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(testUser)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ User creation successful');
        console.log('👤 Created user:', result.data.user.username);
        return result.data.user;
      } else {
        const error = await response.json();
        console.error('❌ User creation failed:', error.message || 'Unknown error');
      }
    } catch (error) {
      console.error('❌ Error creating user:', error);
    }
    
    return null;
  }

  /**
   * Run comprehensive admin dashboard tests
   */
  static async runComprehensiveTests(): Promise<void> {
    console.log('🚀 Starting comprehensive admin dashboard tests...');
    
    await this.testAdminEndpoints();
    await this.validateDashboardData();
    await this.testUserManagement();
    
    console.log('✨ All tests completed!');
  }
}

// Make it available globally for browser console testing
declare global {
  interface Window {
    AdminTestHelper: typeof AdminTestHelper;
  }
}
window.AdminTestHelper = AdminTestHelper;

export default AdminTestHelper;